import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Watch } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatWatchLabel, pickPrimaryWatch } from '@/lib/watch-models'

export interface WatchPillItem {
  address: string
  name: string | null
  isParlent: boolean
}

interface WatchPillsProps {
  watches: WatchPillItem[]
  emptyLabel?: string
}

interface PanelPosition {
  top: number
  left: number
}

const PANEL_WIDTH = 256
const PANEL_MAX_HEIGHT = 280
const VIEWPORT_MARGIN = 8

/** Jeda tutup saat kursor berpindah dari pill ke panel (ada celah 4px). */
const CLOSE_DELAY_MS = 120

/**
 * Ringkas daftar jam milik satu user jadi SATU pill.
 *
 * Satu user bisa punya banyak jam, dan menumpuk semuanya membuat baris tabel
 * memanjang sampai menutupi layar. Jadi hanya satu jam yang tampil sebagai
 * wakil, sisanya diringkas jadi penanda `+N`. Daftar lengkapnya muncul saat
 * di-hover, difokus lewat keyboard, atau diklik.
 *
 * Panel dirender lewat portal ke `document.body` dengan posisi fixed: kontainer
 * DataTable memakai `overflow-hidden` + `overflow-x-auto`, yang akan memotong
 * elemen absolute di dalam sel.
 */
export function WatchPills({ watches, emptyLabel = 'Belum pernah pairing' }: WatchPillsProps) {
  const [position, setPosition] = useState<PanelPosition | null>(null)
  // Dibedakan supaya panel yang dibuka lewat klik tidak tertutup begitu kursor
  // bergeser keluar dari pill.
  const [isPinned, setIsPinned] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const isOpen = position !== null

  const computePosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return

    const rect = anchor.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow

    setPosition({
      top: openUpward ? rect.top - PANEL_MAX_HEIGHT - 4 : rect.bottom + 4,
      left: Math.min(rect.left, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN),
    })
  }, [])

  const close = useCallback(() => {
    setPosition(null)
    setIsPinned(false)
  }, [])

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current === null) return
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }, [])

  /// Tutup setelah jeda, supaya kursor sempat menyeberang dari pill ke panel.
  const scheduleClose = useCallback(() => {
    cancelScheduledClose()
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null
      setIsPinned((pinned) => {
        if (!pinned) setPosition(null)
        return pinned
      })
    }, CLOSE_DELAY_MS)
  }, [cancelScheduledClose])

  // Bersihkan timer kalau baris tabel dilepas saat panel sedang terbuka.
  useEffect(() => cancelScheduledClose, [cancelScheduledClose])

  useEffect(() => {
    if (!isOpen) return

    function handlePointerDown(event: MouseEvent) {
      const target = event.target as Node
      if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) return
      close()
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close()
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    // Posisi fixed tidak ikut bergerak saat halaman digulir, jadi panel ditutup.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [isOpen, close])

  if (watches.length === 0) {
    return <span className="text-xs italic text-stone-600">{emptyLabel}</span>
  }

  const primary = pickPrimaryWatch(watches)
  if (!primary) return null
  const extraCount = watches.length - 1

  const pill = (
    <span
      className={cn(
        'inline-flex w-fit max-w-[11rem] items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium',
        primary.isParlent
          ? 'border-orange-500/40 bg-orange-500/10 text-orange-300'
          : 'border-stone-700 bg-stone-900 text-stone-400',
      )}
      title={primary.address}
    >
      <Watch className="h-3 w-3 shrink-0" />
      <span className="truncate">{formatWatchLabel(primary.name)}</span>
    </span>
  )

  if (extraCount === 0) return pill

  return (
    <div
      ref={anchorRef}
      className="inline-flex items-center gap-1.5"
      onMouseEnter={() => {
        cancelScheduledClose()
        computePosition()
      }}
      onMouseLeave={scheduleClose}
    >
      {pill}
      <button
        type="button"
        onClick={() => {
          if (isPinned) {
            close()
            return
          }
          computePosition()
          setIsPinned(true)
        }}
        onFocus={() => {
          cancelScheduledClose()
          computePosition()
        }}
        onBlur={scheduleClose}
        aria-expanded={isOpen}
        aria-label={`Tampilkan ${watches.length} jam milik user ini`}
        className="shrink-0 rounded-lg border border-stone-700 bg-stone-900 px-2 py-1 text-xs font-semibold text-stone-400 transition hover:border-stone-600 hover:text-stone-200"
      >
        +{extraCount}
      </button>

      {position &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Daftar jam"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: PANEL_WIDTH,
              maxHeight: PANEL_MAX_HEIGHT,
            }}
            className="z-50 overflow-y-auto rounded-xl border border-stone-700 bg-[#141414] p-2 shadow-2xl"
            onMouseEnter={cancelScheduledClose}
            onMouseLeave={scheduleClose}
          >
            <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500">
              {watches.length} jam
            </p>
            <ul className="space-y-1">
              {watches.map((watch) => (
                <li
                  key={watch.address}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-stone-900"
                >
                  <Watch
                    className={cn(
                      'h-3 w-3 shrink-0',
                      watch.isParlent ? 'text-orange-400' : 'text-stone-500',
                    )}
                  />
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'block truncate text-xs font-medium',
                        watch.isParlent ? 'text-orange-300' : 'text-stone-300',
                      )}
                    >
                      {formatWatchLabel(watch.name)}
                    </span>
                    <span className="block truncate font-mono text-[10px] text-stone-500">
                      {watch.address}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  )
}
